'use client';

import { useState, isValidElement, useEffect } from 'react';
import { Arrow } from '@/ui/common/Arrows/ArrowDropdown';
import { scrollToId, scrollToIdWithMargin } from '@/service/utils/scrollToId';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { useSearchParams } from 'next/navigation';
import { cn } from '@/service/utils/cn';

type TAccordionProps = {
    data: {
        title: string;
        content?: string | JSX.Element;
        id: string;
        className?: string;
    }[];
    className?: string;
    clearStyle?: boolean;
};

export const Accordion = ({ data, className, clearStyle }: TAccordionProps) => {
    const [isOpen, setIsOpen] = useState<number | undefined>(undefined);
    const [isClose, setIsClose] = useState<number | undefined>();
    const { isLightframePage } = usePartnerStore();
    const query = useSearchParams();

    const scrollToTab = (el: HTMLElement) => {
        setTimeout(() => {
            isLightframePage ? scrollToId(el.id, 'start') : scrollToIdWithMargin(el.id);
        }, 100);
    };

    const changeOpen = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>, idx: number) => {
        setIsClose(isOpen);
        setTimeout(() => {
            setIsOpen((prev) => {
                if (prev === idx) {
                    return undefined;
                }

                scrollToTab(e.target as HTMLElement);
                return idx;
            });
            setIsClose(undefined);
        }, 400);
    };

    useEffect(() => {
        const tab = query.get('tab');

        if (tab) {
            const elementIndex = data.findIndex(({ id }) => id === tab);
            if (elementIndex >= 0) {
                const el = document.getElementById(tab);

                if (el) {
                    changeOpen(
                        { target: el } as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>,
                        elementIndex
                    );
                } else {
                    setIsOpen(elementIndex);
                }
            }
        } else {
            setIsOpen(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={cn(`accordion mx-4 md:mx-0`, className)} data-test-id="activity-accordion">
            {data.map(({ title, content, id, className }, idx) => {
                const isReactComponent = isValidElement(content);

                content = isReactComponent ? (
                    content
                ) : (
                    <div
                        className="[&>a]:text-brand [&>a]:underline"
                        dangerouslySetInnerHTML={{ __html: content } as { __html: string }}
                    ></div>
                );

                return (
                    <div
                        key={title}
                        className={cn(
                            `duration-1000 ${
                                isOpen === idx
                                    ? 'shadow-light dark:shadow-light'
                                    : 'shadow-lightCard dark:shadow-darkCard'
                            } accordion-item mt-3 overflow-hidden rounded-2xl text-gray-1 dark:text-white`,
                            className,
                            clearStyle && '!bg-transparent !shadow-none'
                        )}
                        data-test-id="activity-tab"
                    >
                        <div
                            id={id}
                            className={cn(
                                'flex cursor-pointer justify-between p-[18px_14px_18px_20px] md:p-[24px_22px_24px_30px]',
                                clearStyle && 'p-2 md:p-2'
                            )}
                            onClick={(e) => {
                                changeOpen(e, idx);
                            }}
                        >
                            <h2
                                data-test-id="activity-tab-title"
                                className={cn(
                                    'tx-large-semibold text-gray-1 dark:text-white',
                                    clearStyle && 'tx-large-regular text-gray-2'
                                )}
                            >
                                <b>{title}</b>
                            </h2>

                            <Arrow
                                toTop={isOpen === idx}
                                className="duration-400 ml-2 inline-block w-[20px] shrink-0"
                            />
                        </div>
                        <div
                            className={cn(
                                'border-grey-4 mx-5 border-t py-5 text-sm text-gray-2 dark:text-white md:mx-7 md:py-6',
                                isOpen !== idx && isClose !== idx && 'hidden',
                                isClose === idx && 'max-h-0 animate-rollYClose',
                                clearStyle && 'mx-2 md:mx-2'
                            )}
                            data-test-id="activity-tab-content"
                        >
                            {isOpen === idx && content}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
